import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";
import logo from "@/assets/logo.png";

const fieldCls = "w-full h-12 px-4 pr-12 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary";
const labelCls = "text-xs font-bold uppercase tracking-[0.15em] text-black/60";

export default function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (newPw.length < 6) { setErr("Choose a password with at least 6 characters."); return; }
    if (newPw !== confirmPw) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/password/reset", { token, new_password: newPw });
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5 bg-background">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={logo} alt="Short Stack Hawai'i" className="w-56 h-auto mb-2" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-black/50">Crew Operations</p>
        </div>
        <div className="bg-white border-2 border-black rounded-xl hard-shadow p-6" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center space-y-3">
              <h2 className="font-display text-2xl font-bold">Invalid link</h2>
              <p className="text-sm text-black/60">This reset link is missing or invalid. Please request a new one from the login screen.</p>
              <a href="/" className="press inline-block h-12 leading-[3rem] px-6 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm">Go to login</a>
            </div>
          ) : done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle size={48} weight="fill" className="text-primary mx-auto" />
              <h2 className="font-display text-2xl font-bold">Password updated</h2>
              <p className="text-sm text-black/60">You can now sign in with your new password.</p>
              <a href="/" data-testid="reset-goto-login" className="press inline-block h-12 leading-[3rem] px-6 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm">Go to login</a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <h2 className="font-display text-2xl font-bold mb-1">Choose a new password</h2>
              <p className="text-sm text-black/60 mb-2">Enter a new password for your account.</p>
              <div>
                <label className={labelCls}>New password</label>
                <div className="relative mt-1">
                  <input data-testid="reset-newpw-input" type={show ? "text" : "password"} autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required className={fieldCls} placeholder="At least 6 characters" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/50">{show ? <EyeSlash size={20} /> : <Eye size={20} />}</button>
                </div>
              </div>
              <div>
                <label className={labelCls}>Confirm new password</label>
                <input data-testid="reset-confirmpw-input" type={show ? "text" : "password"} autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required className="w-full h-12 px-4 mt-1 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary" placeholder="Re-enter password" />
              </div>
              {err && <p data-testid="reset-error" className="text-sm font-bold text-primary">{err}</p>}
              <button data-testid="reset-submit-btn" type="submit" disabled={busy} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60">{busy ? "Updating…" : "Update password"}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
